import { ScrollingModule } from '@angular/cdk/scrolling';
import { DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import localeDECH from '@angular/common/locales/de-CH';
import localeDECHExtra from '@angular/common/locales/extra/de-CH';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  DateFnsModule,
  MatDateFnsModule,
} from '@angular/material-date-fns-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import {
  MAT_CHECKBOX_DEFAULT_OPTIONS,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import {
  MAT_RADIO_DEFAULT_OPTIONS,
  MatRadioModule,
} from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import {
  MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { de } from 'date-fns/locale';
import { NgOtpInputModule } from 'ng-otp-input';
import { AdfsService } from './adfs/adfs.service';
import { AgencyPrioritiesComponent } from './admin/agency-priorities/agency-priorities.component';
import { AssignmentDefaultsComponent } from './admin/assignmentDefaults/assignment-defaults.component';
import { CancelReasonsComponent } from './admin/cancelReasons/cancel-reasons.component';
import { CertificateGroupsComponent } from './admin/certificate-groups/certificate-groups.component';
import { CustomCriteriaComponent } from './admin/custom-criteria/custom-criteria.component';
import { CustomerFlagsComponent } from './admin/customerFlags/customer-flags.component';
import { DemandReasonsComponent } from './admin/demandReasons/demand-reasons.component';
import { JobRequestApprovalComponent } from './admin/jobRequestApproval/job-request-approval.component';
import { PerigonMappingComponent } from './admin/perigonMapping/perigon-mapping.component';
import { PoolerTypesComponent } from './admin/poolerType/pooler-types.component';
import { SharedPoolSourceSettingsComponent } from './admin/shared-pool-source-settings/shared-pool-source-settings.component';
import { TariffsComponent } from './admin/tariffs/tariffs.component';
import { AdminReportsComponent } from './admin/reports/admin-reports.component';
import { ConditionalTransfersComponent } from './admin/transfer-conditions/conditional-transfers.component';
import { AppComponent } from './app.component';
import { AuthGuard, routes } from './app.routes';
import { AvailabilitiesUserListComponent } from './customers/availability-management/availabilities-user-list/availabilities-user-list.component';
import { AvailabilityManagement } from './customers/availability-management/availability-management.component';
import { AvailabilitySelectionDialogComponent } from './customers/availability-management/availability-selection-dialog/availability-selection-dialog.component';
import { UserManagementFilterComponent } from './customers/availability-management/user-management-filter/user-management-filter.component';
import { CustomerUsersListItemComponent } from './customers/customer-users/customer-users-list-item/customer-users-list-item.component';
import {
  AddUserDialog,
  CustomerUsersComponent,
} from './customers/customer-users/customer-users.component';
import { EmploymentEditComponent } from './employments/employment-edit/employment-edit.component';
import { AddEmploymentInviteeComponent } from './employments/employment-invitee-form/add-employment-invitee.component';
import { EmploymentListItemComponent } from './employments/employment-list-item/employment-list-item.component';
import { EmploymentUserLogsBottomSheetComponent } from './employments/employment-user-logs-bottom-sheet/employment-user-logs-bottom-sheet.component';
import { EmploymentsIndexComponent } from './employments/employments-index/employments-index.component';
import { PoolerEmploymentListItemComponent } from './employments/pooler-employment-list-item/pooler-employment-list-item.component';
import { PoolerEmploymentsIndexComponent } from './employments/pooler-employments-index/pooler-employments-index.component';
import { JobMatrixComponent } from './job-matrix/job-matrix.component';
import { DispoJobsIndexComponent } from './jobs/dispo-jobs-index/dispo-jobs-index.component';
import { AddInviteeComponent } from './jobs/invitee-form/add-invitee.component';
import { AgentsInfoDialog } from './jobs/job-edit/dialogs/agents-info-dialog/agents-info-dialog.component';
import { CancelDialog } from './jobs/job-edit/dialogs/cancel-dialog/cancel-dialog.component';
import { DeclineInviteeDialog } from './jobs/job-edit/dialogs/decline-invitee-dialog/decline-invitee-dialog.component';
import { EditShiftsDialog } from './jobs/job-edit/dialogs/edit-shifts-dialog/edit-shifts-dialog.component';
import { EditTimeDialog } from './jobs/job-edit/dialogs/edit-time-dialog/edit-time-dialog.component';
import { ManualCancelTimeDialogComponent } from './jobs/job-edit/dialogs/manual-cancel-time-dialog/manual-cancel-time-dialog.component';
import { TransferInviteeDialog } from './jobs/job-edit/dialogs/transfer-invitee-dialog/transfer-invitee-dialog.component';
import { JobEditComponent } from './jobs/job-edit/job-edit.component';
import { JobMultidayEditComponent } from './jobs/job-multiday-edit/job-multiday-edit.component';
import { JobRequestEditComponent } from './jobs/job-request-edit/job-request-edit.component';
import { JobRequestInviteeComponent } from './jobs/job-requests/job-request-item/job-request-invitee/job-request-invitee.component';
import { JobRequestItemComponent } from './jobs/job-requests/job-request-item/job-request-item.component';
import { JobRequestsComponent } from './jobs/job-requests/job-requests.component';
import { JobShiftsComponent } from './jobs/job-shifts/job-shifts.component';
import { JobsIndexComponent } from './jobs/jobs-index/jobs-index.component';
import { PoolerJobsIndexComponent } from './jobs/pooler-jobs-index/pooler-jobs-index.component';
import { TimeTrackingFormComponent } from './jobs/time-tracking-form/time-tracking-form.component';
import { TranscribeJobListItemDetailsTableComponent } from './jobs/transcribe-job-list/transcribe-job-list-item-details-table/transcribe-job-list-item-details-table.component';
import { TranscribeJobListComponent } from './jobs/transcribe-job-list/transcribe-job-list.component';
import { MsalAuthService } from './msal/msal-auth.service';
import { MsalService } from './msal/msal.service';
import { OrganisationIndexComponent } from './organisations/organisation-index';
import { ConvertToDateObjectPipe } from './pipes/convert-to-date-object.pipe';
import { DbDateToJsDate } from './pipes/db-date-to-js-date';
import { DisplayDatePipe } from './pipes/display-date.pipe';
import { DisplayDbDatePipe } from './pipes/display-db-date.pipe';
import { FormatDistancePipe } from './pipes/format-distance.pipe';
import { FormatMilitaryTimeNumberPipe } from './pipes/format-military-time-number.pipe';
import { I18nPipeModule } from './pipes/i18n-pipe.module';
import { ObjectToArrayPipe } from './pipes/object-to-array.pipe';
import {
  TransferTimePipe,
  TransferTimePipeWithDate,
} from './pipes/transferTime.pipe';
import { UtcToZonedTimePipe } from './pipes/utcToZonedTime.pipe';
import { PlanningMatrixComponent } from './planningMatrix/planning-matrix.component';
import { ReportsIndexComponent } from './reports/reports-index';
import { ToastComponent } from './services/toast/toast.component';
import { ToastService } from './services/toast/toast.service';
import { TranslationService } from './services/translation.service';
import { AvailabilitiesUserListSkeletonComponent } from './shared/availabilities-user-list-skeleton/availabilities-user-list-skeleton.component';
import { AvailabilityDayItemComponent } from './shared/availability-day-item/availability-day-item.component';
import { AvailabilityMonthItemComponent } from './shared/availability-month-item/availability-month-item.component';
import { AvailabilityWeekItemComponent } from './shared/availability-week-item/availability-week-item.component';
import { CertificateMultiselectComponent } from './shared/certificate-multiselect/certificate-multiselect.component';
import { CertificateSelectComponent } from './shared/certificate-select/certificate-select.component';
import { ConfirmWithTextInputDialogComponent } from './shared/confirm-with-text-input-dialog/confirm-with-text-input-dialog.component';
import { ConfirmationDialog } from './shared/confirmation-dialog/confirmation.component';
import { CustomerMultiSelectComponent } from './shared/customer-multi-select/customer-multi-select.component';
import { CustomerSelectComponent } from './shared/customer-select/customer-select.component';
import { EditDateRangeDialog } from './shared/date-range-dialog/date-range-dialog.component';
import { DateRangeSelectComponent } from './shared/date-range-select/date-range-select.component';
import { DateRangesInputComponent } from './shared/date-ranges-input/date-ranges-input.component';
import { DemandReasonSelectComponent } from './shared/demand-reason-select/demand-reason-select.component';
import { DepartmentSelectComponent } from './shared/department-select/department-select.component';
import { DispoEmploymentDetailsTableComponent } from './shared/dispo-employment-details-table/dispo-employment-details-table.component';
import { DispoEmploymentListItemSkeletonComponent } from './shared/dispo-employment-list-item-skeleton/dispo-employment-list-item-skeleton.component';
import { DispoJobListItemCompactContentComponent } from './shared/dispo-job-list-item-compact-content/dispo-job-list-item-compact-content.component';
import { DispoJobListItemDetailsTableComponent } from './shared/dispo-job-list-item-details-table/dispo-job-list-item-details-table.component';
import { DispoJobListItemSkeletonComponent } from './shared/dispo-job-list-item-skeleton/dispo-job-list-item-skeleton.component';
import { DispoJobListItemComponent } from './shared/dispo-job-list-item/dispo-job-list-item.component';
import { EditableShiftTagsComponent } from './shared/editable-shift-tags/editable-shift-tags.component';
import { EmploymentAvailabilitiesUserListSkeletonComponent } from './shared/employment-availabilities-user-list-skeleton/employment-availabilities-user-list-skeleton.component';
import { EmploymentCanceledInfoComponent } from './shared/employment-canceled-info/employment-canceled-info.component';
import { EmploymentCountsComponent } from './shared/employment-counts/employment-counts.component';
import { EmploymentDateComponent } from './shared/employment-date/employment-date.component';
import { EmploymentInviteeListItemComponent } from './shared/employment-invitee-list-item/employment-invitee-list-item.component';
import { EmploymentInviteeStatusIconComponent } from './shared/employment-invitee-status-icon/employment-invitee-status-icon.component';
import { EmploymentOrganisationHeadingComponent } from './shared/employment-organisation-heading/employment-organisation-heading.component';
import { EmploymentUserListItemComponent } from './shared/employment-user-list-item/employment-user-list-item.component';
import { FooterComponent } from './shared/footer/footer.component';
import { HideAllJobsButtonComponent } from './shared/hide-all-jobs-button/hide-all-jobs-button.component';
import { HeaderComponent } from './shared/header/header.component';
import { CustomerSwitcherComponent } from './shared/header/user-switcher/customer-switcher.component';
import { IconButtonModule } from './shared/icon-button/icon-button.module';
import { InviteeAvailableJobsDialogComponent } from './shared/invitee-available-jobs-dialog/invitee-available-jobs-dialog.component';
import { JobInviteeListItemComponent } from './shared/job-invitee-list/job-invitee-list-item/job-invitee-list-item.component';
import { UserLogsBottomSheetComponent } from './shared/job-invitee-list/job-invitee-list-item/user-logs-bottom-sheet/user-logs-bottom-sheet.component';
import { JobInviteeListComponent } from './shared/job-invitee-list/job-invitee-list.component';
import { JobInviteeStatusIconComponent } from './shared/job-invitee-status-icon/job-invitee-status-icon.component';
import { JobListItemCompactInviteesComponent } from './shared/job-list-item-compact-invitees/job-list-item-compact-invitees.component';
import { JobListItemCountsComponent } from './shared/job-list-item-counts/job-list-item-counts.component';
import { JobListItemDateTimeComponent } from './shared/job-list-item-date/job-list-item-date-time.component';
import { JobListItemOrganisationHeadingComponent } from './shared/job-list-item-organisation-heading/job-list-item-organisation-heading.component';
import { JobListItemTransfersComponent } from './shared/job-list-item-transfers/job-list-item-transfers.component';
import { JobPartialTimeChipComponent } from './shared/job-partial-time-chip/job-partial-time-chip.component';
import { JobPossibleUserListHeaderComponent } from './shared/job-possible-users-list/job-possible-user-list-header/job-possible-user-list-header.component';
import { JobPossibleUsersListComponent } from './shared/job-possible-users-list/job-possible-users-list.component';
import { JobPossibleUserListItemComponent } from './shared/job-possible-users-list/job-user-list-item/job-possible-user-list-item.component';
import { JobTypeMultiselectComponent } from './shared/job-type-multiselect/job-type-multiselect.component';
import { JobTypeSelectComponent } from './shared/job-type-select/job-type-select.component';
import { LanguageSelectComponent } from './shared/language-select/language-select.component';
import { MobilitySelectComponent } from './shared/mobility-select/mobility-select.component';
import { AccordionModule } from './shared/modules/accordion/accordion.module';
import { AvailabilityIconModule } from './shared/modules/availability-icon/availability-icon.module';
import { AvailabilityShiftToggleListModule } from './shared/modules/availability-shift-toggle-list/availability-shift-toggle-list.module';
import { CustomDateTableModule } from './shared/modules/custom-date-table/custom-date-table.module';
import { CustomTableModule } from './shared/modules/custom-table/custom-table.module';
import { DepartmentMultiselectModule } from './shared/modules/department-multiselect/department-multiselect.module';
import { EnumToArrayPipeModule } from './shared/modules/enum-to-array-pipe/enum-to-array-pipe.module';
import { KeysToArrayPipeModule } from './shared/modules/keys-to-array-pipe/enum-to-array-pipe.module';
import { LastOfMonthPipeModule } from './shared/modules/last-of-month-pipe/last-of-month-pipe.module';
import { OnVisibleModule } from './shared/modules/on-visible/on-visible.module';
import { TabToggleModule } from './shared/modules/tab-toggle/tab-toggle.module';
import { ToggleButtonsModule } from './shared/modules/toggle-buttons/toggle-buttons.module';
import { MonthlyWorkingHoursComponent } from './shared/monthly-working-hours/monthly-working-hours.component';
import { NativeElementInjectorDirective } from './shared/nativeElementDirective';
import { PageNotFoundComponent } from './shared/page-not-found/page-not-found.component';
import { PartialAcceptDialog } from './shared/partial-accept-dialog/partial-accept-dialog.component';
import { FirstUpperCasePipeModule } from './shared/pipes/first-uppercase-pipe.module';
import { PoolerEmploymentDetailsTableComponent } from './shared/pooler-employment-details-table/pooler-employment-details-table.component';
import { PoolerJobListItemCompactContentComponent } from './shared/pooler-job-list-item-compact-content/pooler-job-list-item-compact-content.component';
import { PoolerJobListItemDetailsTableComponent } from './shared/pooler-job-list-item-details-table/pooler-job-list-item-details-table.component';
import { PoolerJobListItemComponent } from './shared/pooler-job-list-item/pooler-job-list-item.component';
import { PoolerTypeMultiSelectComponent } from './shared/pooler-type-multi-select/pooler-type-multi-select.component';
import { PoolerTypeSelectComponent } from './shared/pooler-type-select/pooler-type-select.component';
import { PreviousJobsEmploymentsLogSheetComponent } from './shared/previous-jobs-employments-log-sheet/previous-jobs-employments-log-sheet.component';
import { PreviousJobsLogSheetComponent } from './shared/previous-jobs-log-sheet/previous-jobs-log-sheet.component';
import { PreviousJobsLogs } from './shared/previous-jobs-logs/previous-jobs-logs';
import { RequiredFieldsDialog } from './shared/required-fields-dialog/required-fields.component';
import { SexSelectComponent } from './shared/sex-select/sex-select.component';
import { UserMultiselectComponent } from './shared/user-multiselect/user-multiselect.component';
import { ShiftSelectComponent } from './shared/shift-select/shift-select.component';
import { SidenavContentComponent } from './shared/sidenav-content/sidenav-content.component';
import { SidenavService } from './shared/sidenav.service';
import { SpecialitySelectComponent } from './shared/speciality-select/speciality-select.component';
import { SupportComponent } from './shared/support/support.component';
import { TagSelectComponent } from './shared/tag-select/tag-select.component';
import { TextSearchComponent } from './shared/text-search/text-search.component';
import { TimeTrackingStepperComponent } from './shared/time-tracking/time-tracking-stepper/time-tracking-stepper.component';
import { TimeTrackingComponent } from './shared/time-tracking/time-tracking.component';
import { TimePipe } from './shared/time.pipe';
import { UploadFileComponent } from './shared/upload-file/upload-file.component';
import { AvailabilityPercentageBar } from './shared/user-details/availability-percentage-bar/availability-percentage-bar';
import { EmploymentMiniAvailabilityMatch } from './shared/user-details/employment-mini-availability-match/employment-mini-availability-match.component';
import { UserListItemDetailsComponent } from './shared/user-details/user-list-item-details.component';
import { UserSelectComponent } from './shared/user-select/user-select.component';
import { SuperAdminDemoSettingsComponent } from './super-admin/demo-settings/demo-settings.component';
import { SwitchToLiveDialogComponent } from './super-admin/demo-settings/switch-to-live-dialog/switch-to-live-dialog.component';
import { SuperAdminSettingsComponent } from './super-admin/super-admin-settings/super-admin-settings.component';
import { EmploymentAvailabilityEditDialogComponent } from './temp-work/availability-edit/availability-edit-dialog/availability-edit-dialog.component';
import { EmploymentAvailabilityEditComponent } from './temp-work/availability-edit/availability-edit.component';
import { CreateEmploymentDialogComponent } from './temp-work/availability-edit/create-employment-dialog/create-employment-dialog.component';
import { EmploymentAvailabilityWorkingJobDialogComponent } from './temp-work/availability-edit/employment-availability-working-job-dialog/employment-availability-working-job-dialog';
import { CustomAvailabilityTableModule } from './temp-work/availability-management/availabilities-user-list/custom-availability-table/custom-availability-table.module';
import { EmploymentAvailabilitiesUserListComponent } from './temp-work/availability-management/availabilities-user-list/employment-availabilities-user-list.component';
import { EmploymentAvailabilityManagement } from './temp-work/availability-management/availability-management.component';
import { EmploymentAvailabilityManagementFilterComponent } from './temp-work/availability-management/employment-availability-management-filter/employment-availability-management-filter.component';
import { EmploymentSelectedAvailabilityDialogComponent } from './temp-work/availability-management/employment-selected-availability-dialog/employment-selected-availability-dialog.component';
import { AvailabilityEditDialogComponent } from './users/availability-edit/availability-edit-dialog/availability-edit-dialog.component';
import { AvailabilityEditComponent } from './users/availability-edit/availability-edit.component';
import { UserAvailabilityCalendarComponent } from './users/availability-edit/user-availability-calendar/user-availability-calendar.component';
import { CompleteProfileComponent } from './users/complete-profile/complete-profile.component';
import { ConfirmComponent } from './users/confirm/confirm.component';
import { UsersDashboardComponent } from './users/dashboard/dashboard.component';
import { DemoUserSetupModalComponent } from './users/demo-user-setup-modal/demo-user-setup-modal.component';
import { ForgotPasswordFormComponent } from './users/forgot-password-form/forgot-password-form.component';
import { LoginFormComponent } from './users/login-form/login-form.component';
import { LogoutComponent } from './users/logout/logout.component';
import { CvUploadComponent } from './users/profile-edit/cv-upload/cv-upload.component';
import { FeaturePermissionComponent } from './users/profile-edit/feature-permission/feature-permission.component';
import { ProfileEditComponent } from './users/profile-edit/profile-edit.component';
import { RegisterDemoComponent } from './users/register-demo/register-demo.component';
import { RegisterComponent } from './users/register/register.component';
import { ResetPasswordFormComponent } from './users/reset-password-form/reset-password-form.component';
import { TwoFactorAuthenticationComponent } from './users/two-factor-authentication/two-factor-authentication.component';

registerLocaleData(localeDECH, 'de-CH', localeDECHExtra);

@NgModule({
  imports: [
    I18nPipeModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' }),
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatCardModule,
    MatInputModule,
    MatListModule,
    MatIconModule,
    MatProgressBarModule,
    MatDatepickerModule,
    MatRadioModule,
    MatBottomSheetModule,
    MatAutocompleteModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatMenuModule,
    MatStepperModule,
    MatToolbarModule,
    MatTooltipModule,
    MatExpansionModule,
    MatTreeModule,
    MatDialogModule,
    ScrollingModule,
    MatBadgeModule,
    MatProgressSpinnerModule,
    HttpClientModule,
    MatSliderModule,
    ReactiveFormsModule,
    AccordionModule,
    IconButtonModule,
    DateFnsModule,
    MatDateFnsModule,
    OnVisibleModule,
    DepartmentMultiselectModule,
    TabToggleModule,
    CustomTableModule,
    CustomDateTableModule,
    AvailabilityIconModule,
    ToggleButtonsModule,
    EnumToArrayPipeModule,
    KeysToArrayPipeModule,
    AvailabilityShiftToggleListModule,
    MatSlideToggleModule,
    NgOtpInputModule,
    MatTableModule,
    MatTabsModule,
    CustomAvailabilityTableModule,
    FirstUpperCasePipeModule,

    LastOfMonthPipeModule,
  ],
  declarations: [
    AddUserDialog,
    EditTimeDialog,
    EditShiftsDialog,
    EditDateRangeDialog,
    CancelDialog,
    DeclineInviteeDialog,
    AgentsInfoDialog,
    UserLogsBottomSheetComponent,
    AppComponent,
    AvailabilityDayItemComponent,
    AvailabilityEditComponent,
    AvailabilityMonthItemComponent,
    AvailabilityWeekItemComponent,
    ConfirmationDialog,
    ConfirmWithTextInputDialogComponent,
    CustomerUsersComponent,
    AvailabilityManagement,
    FooterComponent,
    ForgotPasswordFormComponent,
    HeaderComponent,
    HideAllJobsButtonComponent,
    JobEditComponent,
    JobMultidayEditComponent,
    JobsIndexComponent,
    LoginFormComponent,
    NativeElementInjectorDirective,
    PageNotFoundComponent,
    ProfileEditComponent,
    ResetPasswordFormComponent,
    JobMatrixComponent,
    SidenavContentComponent,
    ToastComponent,
    JobPossibleUserListHeaderComponent,
    JobPossibleUserListItemComponent,
    UsersDashboardComponent,
    JobPossibleUsersListComponent,
    TimePipe,
    LogoutComponent,
    SupportComponent,
    ReportsIndexComponent,
    OrganisationIndexComponent,
    ConfirmComponent,
    ObjectToArrayPipe,
    TransferTimePipe,
    TransferTimePipeWithDate,
    UtcToZonedTimePipe,
    RegisterComponent,
    RegisterDemoComponent,
    DemoUserSetupModalComponent,
    CompleteProfileComponent,
    TimeTrackingComponent,
    TimeTrackingStepperComponent,
    AvailabilitiesUserListComponent,
    DepartmentSelectComponent,
    UserManagementFilterComponent,
    SpecialitySelectComponent,
    CertificateSelectComponent,
    TagSelectComponent,
    TextSearchComponent,
    UserAvailabilityCalendarComponent,
    AvailabilityEditDialogComponent,
    DateRangeSelectComponent,
    AddInviteeComponent,
    UserListItemDetailsComponent,
    UploadFileComponent,
    CvUploadComponent,
    CustomerSwitcherComponent,
    TwoFactorAuthenticationComponent,
    CustomerUsersListItemComponent,
    FeaturePermissionComponent,
    UserSelectComponent,
    TransferInviteeDialog,
    PartialAcceptDialog,
    TimeTrackingFormComponent,
    JobInviteeListComponent,
    JobInviteeListItemComponent,
    PreviousJobsLogSheetComponent,
    PreviousJobsEmploymentsLogSheetComponent,
    PreviousJobsLogs,
    EmploymentAvailabilityManagement,
    EmploymentAvailabilitiesUserListComponent,
    EmploymentSelectedAvailabilityDialogComponent,
    EmploymentAvailabilityEditComponent,
    EmploymentAvailabilityEditDialogComponent,
    ManualCancelTimeDialogComponent,
    CreateEmploymentDialogComponent,
    InviteeAvailableJobsDialogComponent,
    EmploymentAvailabilityWorkingJobDialogComponent,
    EmploymentAvailabilityManagementFilterComponent,
    EmploymentsIndexComponent,
    EmploymentEditComponent,
    EmploymentListItemComponent,
    DisplayDbDatePipe,
    DbDateToJsDate,
    DisplayDatePipe,
    ConvertToDateObjectPipe,
    FormatMilitaryTimeNumberPipe,
    FormatDistancePipe,
    CustomerMultiSelectComponent,
    SexSelectComponent,
    PoolerTypeSelectComponent,
    PoolerTypeMultiSelectComponent,
    MobilitySelectComponent,
    LanguageSelectComponent,
    EmploymentDateComponent,
    EmploymentCountsComponent,
    DispoEmploymentDetailsTableComponent,
    PoolerEmploymentDetailsTableComponent,
    EmploymentOrganisationHeadingComponent,
    EmploymentUserListItemComponent,
    EmploymentInviteeListItemComponent,
    EmploymentInviteeStatusIconComponent,
    EmploymentCanceledInfoComponent,
    EmploymentMiniAvailabilityMatch,
    PoolerEmploymentsIndexComponent,
    PoolerEmploymentListItemComponent,
    EmploymentUserLogsBottomSheetComponent,
    AvailabilityPercentageBar,
    AddEmploymentInviteeComponent,
    DispoJobListItemComponent,
    PoolerJobListItemComponent,
    JobListItemCountsComponent,
    JobListItemDateTimeComponent,
    JobListItemOrganisationHeadingComponent,
    DispoJobListItemDetailsTableComponent,
    PoolerJobListItemDetailsTableComponent,
    PoolerJobsIndexComponent,
    DispoJobsIndexComponent,
    JobInviteeStatusIconComponent,
    DateRangesInputComponent,
    JobPartialTimeChipComponent,
    TariffsComponent,
    AssignmentDefaultsComponent,
    DispoJobListItemSkeletonComponent,
    DispoEmploymentListItemSkeletonComponent,
    EmploymentAvailabilitiesUserListSkeletonComponent,
    AvailabilitiesUserListSkeletonComponent,
    JobRequestsComponent,
    JobRequestEditComponent,
    JobRequestItemComponent,
    JobRequestInviteeComponent,
    CustomerSelectComponent,
    DateRangesInputComponent,
    EditableShiftTagsComponent,
    CustomerFlagsComponent,
    SuperAdminSettingsComponent,
    SuperAdminDemoSettingsComponent,
    SwitchToLiveDialogComponent,
    PoolerTypesComponent,
    JobRequestApprovalComponent,
    TranscribeJobListComponent,
    TranscribeJobListItemDetailsTableComponent,
    PlanningMatrixComponent,
    AvailabilitySelectionDialogComponent,
    DemandReasonsComponent,
    CancelReasonsComponent,
    RequiredFieldsDialog,
    JobShiftsComponent,
    ConditionalTransfersComponent,
    AgencyPrioritiesComponent,
    SharedPoolSourceSettingsComponent,
    ShiftSelectComponent,
    UserMultiselectComponent,
    JobTypeSelectComponent,
    JobTypeMultiselectComponent,
    DemandReasonSelectComponent,
    CertificateMultiselectComponent,
    JobListItemTransfersComponent,
    JobListItemCompactInviteesComponent,
    DispoJobListItemCompactContentComponent,
    PoolerJobListItemCompactContentComponent,
    CertificateGroupsComponent,
    CustomCriteriaComponent,
    MonthlyWorkingHoursComponent,
    PerigonMappingComponent,
    AdminReportsComponent,
  ],
  bootstrap: [AppComponent],
  providers: [
    SidenavService,
    AuthGuard,
    DatePipe,
    ToastService,
    TranslationService,
    AdfsService,
    MsalAuthService,
    MsalService,
    { provide: MAT_DATE_LOCALE, useValue: 'de-CH' },
    { provide: LOCALE_ID, useValue: 'de-CH' },
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: { color: 'primary' },
    },
  ],
})
export class AppModule {
  constructor(
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private dateAdapter: DateAdapter<Date>,
  ) {
    this.dateAdapter.setLocale(de);
    matIconRegistry.addSvgIcon(
      'account-multiple',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/account-multiple.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'account-plus',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/account-plus.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'filter-alt',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/filter-alt.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'filter_alt_outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/filter_alt_outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'star-booked-rebook-filled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/star-booked-rebook-filled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'bed',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/bed.svg'),
    );
    matIconRegistry.addSvgIcon(
      'calendar-question',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar-question.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'note',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/note.svg'),
    );
    matIconRegistry.addSvgIcon(
      'job-request',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/job-request.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'home',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/home.svg'),
    );
    matIconRegistry.addSvgIcon(
      'text-box-multiple',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/text-box-multiple.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'plus-box-multiple',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/plus-box-multiple.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'cog',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/cog.svg'),
    );
    matIconRegistry.addSvgIcon(
      'job-series',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/job-series.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'link',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/link.svg'),
    );
    matIconRegistry.addSvgIcon(
      'link-add',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/link-add.svg'),
    );
    matIconRegistry.addSvgIcon(
      'lock',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/lock.svg'),
    );
    matIconRegistry.addSvgIcon(
      'link-off',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/link-off.svg'),
    );
    matIconRegistry.addSvgIcon(
      'send',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/send.svg'),
    );
    matIconRegistry.addSvgIcon(
      'save',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/save.svg'),
    );
    matIconRegistry.addSvgIcon(
      'list',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/list.svg'),
    );
    matIconRegistry.addSvgIcon(
      'calendar-event',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar-event.svg',
      ),
    );

    matIconRegistry.addSvgIcon(
      'account-multiple-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/account-multiple-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'account-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/account-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'account',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/account.svg'),
    );
    matIconRegistry.addSvgIcon(
      'alert-circle',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/alert-circle.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'alert-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/alert-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'calendar-plus-save-download',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar-plus-save-download.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'arrow-left',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/arrow-left.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'book-open-variant',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/book-open-variant.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'briefcase',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/briefcase.svg'),
    );
    matIconRegistry.addSvgIcon(
      'calendar-check-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar-check-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'calendar-check',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar-check.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'calendar-export',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar-export.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'card-account-details-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/card-account-details-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'car-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/car-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'chart-timeline-variant',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/chart-timeline-variant.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'check',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/check.svg'),
    );
    matIconRegistry.addSvgIcon(
      'check-all',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/check-all.svg'),
    );
    matIconRegistry.addSvgIcon(
      'checkbox-blank-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/checkbox-blank-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'checkbox-intermediate',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/checkbox-intermediate.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'checkbox-marked',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/checkbox-marked.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'check-circle-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/check-circle-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'check-circle-alert',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/check-circle-alert.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'check-circle',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/check-circle.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'chevron-down',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/chevron-down.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'chevron-left',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/chevron-left.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'chevron-right',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/chevron-right.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'clock-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/clock-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'close',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/close.svg'),
    );
    matIconRegistry.addSvgIcon(
      'close-circle-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/close-circle-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'close-circle',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/close-circle.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'cog-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/cog-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'content-copy',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/content-copy.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'currency-usd',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/currency-usd.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'delete-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/delete-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'delete-trash-remove',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/delete-trash-remove.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'dots-vertical',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/dots-vertical.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'download',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/download.svg'),
    );
    matIconRegistry.addSvgIcon(
      'email',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/email.svg'),
    );
    matIconRegistry.addSvgIcon(
      'email-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/email-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'facebook',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/facebook.svg'),
    );
    matIconRegistry.addSvgIcon(
      'google',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/google.svg'),
    );
    matIconRegistry.addSvgIcon(
      'google-translate',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/google-translate.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'help-circle',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/help-circle.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'help-circle-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/help-circle-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'home-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/home-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'human-male-female',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/human-male-female.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'human-male-height-variant',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/human-male-height-variant.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'information-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/information-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'laptop',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/laptop.svg'),
    );
    matIconRegistry.addSvgIcon(
      'lock-open-check',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/lock-open-check.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'logout',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/logout.svg'),
    );
    matIconRegistry.addSvgIcon(
      'magnify',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/magnify.svg'),
    );
    matIconRegistry.addSvgIcon(
      'menu',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/menu.svg'),
    );
    matIconRegistry.addSvgIcon(
      'message-bulleted',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/message-bulleted.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'minus',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/minus.svg'),
    );
    matIconRegistry.addSvgIcon(
      'pencil-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/pencil-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'phone',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/phone.svg'),
    );
    matIconRegistry.addSvgIcon(
      'plus',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/plus.svg'),
    );
    matIconRegistry.addSvgIcon(
      'refresh',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/refresh.svg'),
    );
    matIconRegistry.addSvgIcon(
      'planning-matrix',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/planning-matrix.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'undo',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/undo.svg'),
    );
    matIconRegistry.addSvgIcon(
      'school',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/school.svg'),
    );
    matIconRegistry.addSvgIcon(
      'star',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/star.svg'),
    );
    matIconRegistry.addSvgIcon(
      'star-circle-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/star-circle-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'star-off',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/star-off.svg'),
    );
    matIconRegistry.addSvgIcon(
      'text-box-multiple-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/text-box-multiple-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'tshirt-crew',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/tshirt-crew.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'update',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/update.svg'),
    );
    matIconRegistry.addSvgIcon(
      'account-star-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/account-star-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'star-check-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/star-check-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'shield-lock',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/shield-lock.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'eye-outline',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/eye-outline.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'swap',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/swap.svg'),
    );
    matIconRegistry.addSvgIcon(
      'booked-transferred',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/booked-transferred.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'transferred-cancelled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/transferred-cancelled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'partial-accept',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/partial-accept.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'tag',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/tag.svg'),
    );
    matIconRegistry.addSvgIcon(
      'hospital',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/hospital.svg'),
    );
    matIconRegistry.addSvgIcon(
      'suitcase-job-history-clock',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/suitcase-job-history-clock.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'pending-actions',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/pending-actions.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'clock-forward',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/clock-forward.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'calendar-day-check-availability',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar-day-check-availability.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'calendar_add_on',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar_add_on.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'content_paste',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/content_paste.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'corporate_fare',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/corporate_fare.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'hub',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/hub.svg'),
    );
    matIconRegistry.addSvgIcon(
      'group',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/group.svg'),
    );
    matIconRegistry.addSvgIcon(
      'groups',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/groups.svg'),
    );
    matIconRegistry.addSvgIcon(
      'live_help',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/live_help.svg'),
    );
    matIconRegistry.addSvgIcon(
      'page_info',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/page_info.svg'),
    );
    matIconRegistry.addSvgIcon(
      'profile_icon',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/profile_icon.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'board-mission-transfer-waiting-clock',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/board-mission-transfer-waiting-clock.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'board-mission-transfer-decline-rejected',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/board-mission-transfer-decline-rejected.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'board-mission-transfer-auto-star',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/board-mission-transfer-auto-star.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'task',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/task.svg'),
    );
    matIconRegistry.addSvgIcon(
      'trending_up',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/trending_up.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'work',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/work.svg'),
    );

    matIconRegistry.addSvgIcon(
      'menu-dot',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/menu-dot.svg'),
    );

    matIconRegistry.addSvgIcon(
      'suitcase-job',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/suitcase-job.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'booked-star-circle-filled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/booked-star-circle-filled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'not-available-busy-circle-filled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/not-available-busy-circle-filled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'available-check-circle-filled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/available-check-circle-filled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'waiting-clock-circle-filled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/waiting-clock-circle-filled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'calendar-day-check-availability',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar-day-check-availability.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'calendar_add_on',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/calendar_add_on.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'content_paste',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/content_paste.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'content_paste_go',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/content_paste_go.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'board-job-shift-grid-matrix',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/board-job-shift-grid-matrix.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'board-job-shift-grid-matrix-overview',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/board-job-shift-grid-matrix-overview.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'checklist_rtl',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/checklist_rtl.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'corporate_fare',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/corporate_fare.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'group',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/group.svg'),
    );
    matIconRegistry.addSvgIcon(
      'groups',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/groups.svg'),
    );
    matIconRegistry.addSvgIcon(
      'live_help',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/live_help.svg'),
    );
    matIconRegistry.addSvgIcon(
      'page_info',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/page_info.svg'),
    );
    matIconRegistry.addSvgIcon(
      'profile_icon',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/profile_icon.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'task',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/task.svg'),
    );
    matIconRegistry.addSvgIcon(
      'trending_up',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/trending_up.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'work',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/work.svg'),
    );
    matIconRegistry.addSvgIcon(
      'blocked-storno-circle',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/blocked-storno-circle.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'duplicate-copy',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/duplicate-copy.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'star-rejected-storno-rebook-filled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/star-rejected-storno-rebook-filled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'send',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/send.svg'),
    );
    matIconRegistry.addSvgIcon(
      'add-circle-plus',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/add-circle-plus.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'inbox',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/inbox.svg'),
    );
    matIconRegistry.addSvgIcon(
      'board-booked-star',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/board-booked-star.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'archive-box',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/archive-box.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'check-brackets-partial-accept',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/check-brackets-partial-accept.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'clock',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/clock.svg'),
    );
    matIconRegistry.addSvgIcon(
      'close-rejected-remove-circle',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/close-rejected-remove-circle.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'close-rejected-remove-circle-fill',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/close-rejected-remove-circle-fill.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'star-filled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/star-filled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'star-rejected-storno-filled',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/star-rejected-storno-filled.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'suitcase-job-fill',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/suitcase-job-fill.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'e-mail-read-check',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/e-mail-read-check.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'call',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/call.svg'),
    );
    matIconRegistry.addSvgIcon(
      'cake',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/cake.svg'),
    );
    matIconRegistry.addSvgIcon(
      'badge',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/badge.svg'),
    );
    matIconRegistry.addSvgIcon(
      'directions_car',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/directions_car.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'height',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/height.svg'),
    );
    matIconRegistry.addSvgIcon(
      'language',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/language.svg'),
    );
    matIconRegistry.addSvgIcon(
      'laundry',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/laundry.svg'),
    );
    matIconRegistry.addSvgIcon(
      'menu_book',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/menu_book.svg'),
    );
    matIconRegistry.addSvgIcon(
      'sell',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/sell.svg'),
    );

    matIconRegistry.addSvgIcon(
      'person-user-arrow-transfer',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/person-user-arrow-transfer.svg',
      ),
    );

    matIconRegistry.addSvgIcon(
      'board-mission-transfer-arrow',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/board-mission-transfer-arrow.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'do_not_disturb_on',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/do_not_disturb_on.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'user_attributes',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/user_attributes.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'house-check',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/house-check.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'shooting-star',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/shooting-star.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'auto-book-star',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/auto-book-star.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'certificate',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/proffession-hat-study.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'tiles',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/tiles.svg'),
    );
    matIconRegistry.addSvgIcon(
      'jobs-list',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/jobs-list.svg'),
    );
    matIconRegistry.addSvgIcon(
      'info',
      domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/info.svg'),
    );
    matIconRegistry.addSvgIcon(
      'clock_loader',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/clock_loader.svg',
      ),
    );
    matIconRegistry.addSvgIcon(
      'person_check',
      domSanitizer.bypassSecurityTrustResourceUrl(
        'assets/icons/person_check.svg',
      ),
    );
  }
}
